import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { IconButton, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { alpha } from '@mui/system/colorManipulator';
import { useTheme } from '@mui/material/styles';
import OptionItem from '#component/project/form/alert-confirmation/option-item';
import { isEnabledModule } from '#component/project/form/helper';
import DisabledModuleMessage from '#component/project/form/project/project-modules/disabled-module-message';
import { ButtonContainer, StyledButton, StyledPaper, StyledTitle } from './style';
import { uuidv4 } from '#helper';

/**
 * Renders form with submit, go back
 * and add user buttons.
 * @returns {React.FC}
 */

const Form = props => {
  const theme = useTheme();
  const {
    submitting,
    previousStep,
    nextStep,
    history,
    forceError,
    formModules,
    submitForm,
    saveDisabled
  } = props;

  const { control, watch, handleSubmit, formState: { errors } } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'alertConfirmations',
    keyName: 'fieldId'
  });
  const items = watch('alertConfirmations') ?? [];
  const arrayError = errors.alertConfirmations?.root?.message;

  const shipmentModuleEnabled = isEnabledModule('SHIPMENT', formModules);
  const siteModuleEnabled = isEnabledModule('SITE', formModules);
  const isFormEnabled = shipmentModuleEnabled || siteModuleEnabled;

  const addConfirmationItem = handleSubmit(() => append({ id: uuidv4() }));

  const handleCancelClick = () => {
    history.push('/project');
  };

  return (
    <form autoComplete="off" onSubmit={submitForm}>
      {isFormEnabled ?
        <>
          <StyledTitle variant="h4" gutterBottom>
            Alert confirmation options
          </StyledTitle>
          <StyledPaper>
            {fields.map((field, index) => (
              <OptionItem
                key={field.fieldId}
                item={items[index] ?? field}
                index={index}
                member={`alertConfirmations[${index}]`}
                remove={remove}
                forceError={forceError}
              />
            ))}
            {arrayError &&
              <Typography align="left" color="error">{arrayError}</Typography>}
          </StyledPaper>
        </> : <DisabledModuleMessage />}
      <ButtonContainer isFormEnabled={isFormEnabled}>
        {isFormEnabled ?
          <div>
            <IconButton
              size="small"
              onClick={addConfirmationItem}
              aria-label="Add confirmation option"
            >
              <Tooltip title="Add option">
                <AddCircleOutlineRoundedIcon
                  style={{ color: alpha(theme.palette.secondary.main, 1) }}
                />
              </Tooltip>
            </IconButton>
          </div> : null}
        <div>
          <StyledButton variant="outlined" onClick={handleCancelClick}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="outlined"
            onClick={previousStep}
          >
            Back
          </StyledButton>
          <StyledButton
            disabled={submitting}
            onClick={nextStep}
            variant="outlined"
          >
            Next
          </StyledButton>
          <StyledButton
            disabled={saveDisabled}
            type="submit"
            variant="contained"
            color="secondary"
          >
            Save
          </StyledButton>
        </div>
      </ButtonContainer>
    </form>
  );
};

export default Form;
